import type { LeadCountRow } from "./sales-csv";
import type { Centre, DetailRow, MonthRow } from "../data/mockData";

type ApiErrorBody = { error?: { code?: string; message?: string } };

export class ApiClientError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
  }
}

export type LeadCounts = {
  year: string;
  importedAt: string | null;
  fileName: string | null;
  rows: Array<{ month: string; platformId: string; centre: "Istanbul" | "Cairo" | "Unallocated"; leads: number }>;
};

const requestJson = async <T>(input: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(input, {
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const errorBody = body as ApiErrorBody;
    throw new ApiClientError(
      response.status,
      errorBody.error?.code ?? "REQUEST_FAILED",
      errorBody.error?.message ?? "The request could not be completed.",
    );
  }
  return body as T;
};

export type ReportResponse = {
  year: string;
  centre: string;
  generatedAt: string;
  warnings?: string[];
  totals: { plan: number; spend: number; sales: number; revenue: number; roas: number; spendComplete: boolean };
  months: MonthRow[];
};

export type FxRate = {
  month: string;
  quote_currency: "USD" | "TRY" | "GBP";
  native_per_usd: number;
  period_start: string;
  period_end: string;
  observation_count: number;
  status: "LIVE" | "FROZEN";
  source: string;
  fetched_at: string;
};

export type PlatformImportGroup = "META" | "GOOGLE" | "TIKTOK" | "MICROSOFT" | "X" | "PINTEREST";

export type PlatformImportEntry = {
  month: string;
  platformId: string;
  centre: Centre;
  accountId: string;
  nativeCurrency: "USD" | "TRY" | "GBP";
  nativeAmountMinor: number;
  nativePerUsd: number;
  amountUsdCents: number;
  sourceRows: number;
};

export type PlatformImportPreview = {
  id: string;
  platformGroup: PlatformImportGroup;
  fileName: string;
  status: "PREVIEWED" | "IMPORTED" | "REJECTED";
  rowCount: number;
  entries: PlatformImportEntry[];
  warnings: string[];
  totalUsdCents: number;
  createdAt: string;
  importedAt: string | null;
};


export type SalesImportPreview = {
  id: string;
  status: "PREVIEWED" | "IMPORTED";
  paymentsFile: string;
  leadSourcesFile: string;
  paymentRows: number;
  attributedRows: number;
  unattributedRows: number;
  distinctPatients: number;
  totalRevenueUsdCents: number;
  cohortMonths: { first: string; last: string };
  platformTotals: Array<{ platformId: string | null; label: string; payments: number; revenueUsdCents: number }>;
  warnings: string[];
  duplicateOfImportedJob: boolean;
  previousRevenueUsdCents: number;
  previousPaymentRows: number;
  removedScopes: string[];
};

export type SalesImportStatus = {
  lastImport: null | {
    id: string;
    paymentsFile: string;
    leadSourcesFile: string;
    paymentRows: number;
    attributedRows: number;
    totalRevenueUsdCents: number;
    warnings: string[];
    importedAt: string;
    paymentsThrough: string | null;
    hasPaymentIds: boolean;
  };
  feed: { lastReceivedAt: string | null; payments: number; recent: FeedPayment[] };
};

export type McpConnectionRequest = {
  id: string;
  appName: string;
  destination: string;
  userEmail: string;
  userName: string;
  status: "PENDING" | "APPROVED" | "DENIED" | "COMPLETED" | "EXPIRED";
  requestedAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
};

export type FeedPayment = {
  paymentId: string;
  patientId: string;
  amountUsdCents: number;
  paymentDate: string | null;
  cohortMonth: string;
  leadSource: string;
  platformId: string | null;
  centre: string | null;
  receivedAt: string;
  status: "IN_REPORT" | "IN_EXPORT" | "COVERED_BY_EXPORT" | "NOT_APPLIED";
};


export type FacebookAdNode = {
  id: string;
  name: string;
  spendComplete: boolean;
  spendUsdCents: number;
  clicks: number;
  impressions: number;
  revenueUsdCents: number;
  sales: number;
  children?: FacebookAdNode[];
};

export type FacebookAdsResponse = {
  year: string;
  centre: string;
  campaigns: FacebookAdNode[];
  matchRate: number | null;
  matchedRevenueUsdCents: number;
  unmatchedRevenueUsdCents: number;
  warnings: string[];
};


export type GoogleSearchTerm = {
  month: string;
  centre: Centre;
  campaign: string;
  adGroup: string;
  searchTerm: string;
  clicks: number;
  impressions: number;
  spendUsdCents: number;
};

export type GoogleSearchTermsResponse = {
  year: string;
  centre: string;
  terms: GoogleSearchTerm[];
  totalTerms: number;
  totalSpendUsdCents: number;
  totalClicks: number;
  warnings: string[];
};

export type Freshness = {
  synced: { meta: string | null; google: string | null; tiktok: string | null; microsoft: string | null };
  uploaded: { microsoft: string | null; x: string | null; pinterest: string | null };
  salesImportedAt: string | null;
  salesFeedReceivedAt: string | null;
};

export type MasterEntryType = "PLAN" | "SPEND";

export type MasterEntry = {
  id: string;
  entryType: MasterEntryType;
  month: string;
  platformId: string | null;
  platform: string;
  centre: Centre;
  accountId: string | null;
  currency: "USD" | "TRY" | "GBP" | null;
  nativeAmountMinor: number | null;
  amountUsdCents: number | null;
  source: string;
  sourceReference: string | null;
  accountName: string | null;
  locked: boolean;
  updatedAt: string;
};

export type ComposioSyncRun = {
  id: string;
  platform_group: "META" | "GOOGLE" | "TIKTOK";
  date_from: string;
  date_to: string;
  status: "RUNNING" | "SUCCEEDED" | "FAILED" | "SKIPPED";
  connected_account_id: string | null;
  account_count: number;
  row_count: number;
  error_message: string | null;
  started_at: string;
  finished_at: string | null;
};

export type ComposioPlatformStatus = {
  platformGroup: "META" | "GOOGLE" | "TIKTOK";
  toolkit: string;
  connected: boolean;
  connectionStatus: string;
  lastRun: ComposioSyncRun | null;
};

export type ComposioStatus = {
  configured: boolean;
  automaticSchedule: string;
  platforms: ComposioPlatformStatus[];
};

export type ComposioSyncResult = {
  id: string;
  platformGroup: "META" | "GOOGLE" | "TIKTOK";
  status: "SUCCEEDED" | "SKIPPED";
  dateFrom: string;
  dateTo: string;
  accountCount: number;
  rowCount: number;
};

export type ApprovedUser = {
  id: string;
  email: string;
  name: string;
  role: "ADMINISTRATOR" | "VIEWER";
  active: boolean;
  last_login_at: string | null;
};

export type AgentProposal = {
  id: string;
  kind: "PLAN" | "COHORT";
  status: "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED";
  payload: {
    month: string;
    platformId: string;
    centre: Centre;
    amountUsdCents?: number;
    salesCount?: number;
    revenueInputMinor?: number;
    inputCurrency?: "USD" | "TRY" | "GBP";
  };
  before: Record<string, unknown> | null;
  createdAt: string;
  expiresAt: string;
};

export type AgentStatus = {
  available: boolean;
  openAiConfigured: boolean;
  composioConfigured: boolean;
  agentId: string | null;
  role: "ADMINISTRATOR" | "VIEWER";
  capabilities: string[];
  proposals: AgentProposal[];
};


export type RevenueProofPatient = {
  patientId: string;
  cohortMonth: string;
  platformId: string;
  platformLabel: string;
  centre: Centre;
  centreEstimated: boolean;
  leadSource: string;
  adAccount: string | null;
  country: string | null;
  language: string | null;
  campaign: string | null;
  adSet: string | null;
  ad: string | null;
  leadForm: string | null;
  reason: string;
  totalUsdCents: number;
  payments: Array<{ date: string | null; amountUsdCents: number }>;
};

export type RevenueProof = {
  month: string | null;
  year: string | null;
  channel: string;
  centre: string;
  cohortRule: string;
  utmNote: string;
  summary: { patients: number; payments: number; revenueUsdCents: number };
  truncated: boolean;
  patients: RevenueProofPatient[];
};

export const api = {
  agentStatus: () => requestJson<{ data: AgentStatus }>("/api/agent/status"),

  agentChat: (message: string) => requestJson<{ data: { message: string; sessionId: string; proposals: AgentProposal[] } }>("/api/agent/chat", {
    method: "POST",
    body: JSON.stringify({ message }),
  }),

  resetAgentSession: () => requestJson<{ data: { reset: boolean } }>("/api/agent/session", { method: "DELETE" }),

  approveAgentProposal: (id: string) => requestJson<{ data: AgentProposal }>(`/api/agent/proposals/${encodeURIComponent(id)}/approve`, {
    method: "POST",
    body: JSON.stringify({}),
  }),

  rejectAgentProposal: (id: string) => requestJson<{ data: { id: string; status: "REJECTED" } }>(`/api/agent/proposals/${encodeURIComponent(id)}/reject`, {
    method: "POST",
    body: JSON.stringify({}),
  }),

  session: () => requestJson<{ authenticated: boolean; user: null | { id: string; email: string; name: string; role: "ADMINISTRATOR" | "VIEWER" } }>("/api/session"),

  report: (year: string, centre: "All call centres" | Centre = "All call centres") => {
    const params = new URLSearchParams({ year, centre });
    return requestJson<ReportResponse>(`/api/report?${params}`);
  },

  fx: (year: string) => requestJson<{ year: string; rates: FxRate[] }>(`/api/fx?${new URLSearchParams({ year })}`),

  updatePlans: (entries: Array<{ month: string; platformId: string; centre: Centre; amountUsdCents: number; expectedAmountUsdCents: number }>) =>
    requestJson<{ data: { saved: number } }>("/api/plans/bulk", { method: "PUT", body: JSON.stringify({ entries }) }),
  updatePlan: (entry: { month: string; platformId: string; centre: Centre; amountUsdCents: number }) =>
    requestJson<{ data: typeof entry }>("/api/plans", { method: "PUT", body: JSON.stringify(entry) }),

  users: () => requestJson<{ users: ApprovedUser[] }>("/api/users"),

  mcpConnectionRequests: () => requestJson<{ requests: McpConnectionRequest[] }>("/api/mcp/connection-requests"),

  decideMcpConnection: (id: string, decision: "approve" | "deny") =>
    requestJson<{ id: string; status: string }>(`/api/mcp/connection-requests/${encodeURIComponent(id)}/${decision}`, { method: "POST" }),

  createUser: (user: { email: string; name: string; role: "ADMINISTRATOR" | "VIEWER" }) => requestJson<{ user: typeof user & { id: string; active: boolean } }>("/api/users", {
    method: "POST",
    body: JSON.stringify(user),
  }),

  updateUserRole: (id: string, role: "ADMINISTRATOR" | "VIEWER") => requestJson<{ user: ApprovedUser }>(`/api/users/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  }),

  sources: () => requestJson<{ platforms: Array<{ id: string; label: string; source: string; centres: Centre[]; accounts: Array<{ id: string; external_id: string; name: string; active: boolean; native_currency: string; default_centre: Centre | null; allocation_rule: string }> }> }>("/api/sources"),

  syncFx: (month: string) => requestJson<{ data: { month: string; status: string; rates: Record<string, number> } }>("/api/fx/sync", {
    method: "POST",
    body: JSON.stringify({ month }),
  }),

  syncFxHistory: (from = "2025-01", to = new Date().toISOString().slice(0, 7)) => requestJson<{
    data: { from: string; to: string; synced: string[]; skipped: string[] };
  }>("/api/fx/sync-history", {
    method: "POST",
    body: JSON.stringify({ from, to }),
  }),

  updateFx: (month: string, tryRate: number, gbpRate: number) => requestJson<{
    data: { month: string; tryRate: number; gbpRate: number; status: "LIVE" | "FROZEN"; source: string };
  }>("/api/fx", {
    method: "PUT",
    body: JSON.stringify({ month, tryRate, gbpRate }),
  }),


  facebookAds: (year: string, centre: string) => requestJson<FacebookAdsResponse>(`/api/facebook-ads?${new URLSearchParams({ year, centre })}`),

  googleSearchTerms: (year: string, centre: string) => requestJson<GoogleSearchTermsResponse>(`/api/google-search-terms?${new URLSearchParams({ year, centre })}`),

  freshness: () => requestJson<Freshness>("/api/freshness"),

  revenueProof: (scope: { month?: string; year?: string }, channel: string, centre: string) =>
    requestJson<RevenueProof>(`/api/revenue-proof?${scope.month ? `month=${encodeURIComponent(scope.month)}` : `year=${encodeURIComponent(scope.year ?? "")}`}&channel=${encodeURIComponent(channel)}&centre=${encodeURIComponent(centre)}`),

  salesImportStatus: () => requestJson<SalesImportStatus>("/api/sales-imports/status"),

  previewSalesImport: (payload: { paymentsFile: string; paymentsCsv: string; leadSourcesFile: string; leadSourcesCsv: string }) =>
    requestJson<{ preview: SalesImportPreview }>("/api/sales-imports/preview", { method: "POST", body: JSON.stringify(payload) }),

  commitSalesImport: (payload: { jobId: string; paymentsFile: string; paymentsCsv: string; leadSourcesFile: string; leadSourcesCsv: string; confirmFullSnapshot: boolean }) =>
    requestJson<{ data: { id: string; status: "IMPORTED"; paymentRows: number } }>("/api/sales-imports/commit", { method: "POST", body: JSON.stringify(payload) }),

  leadCounts: (year: string) => requestJson<{ data: LeadCounts }>(`/api/lead-counts?year=${encodeURIComponent(year)}`),

  importLeadCounts: (payload: { fileName: string; contacts: number; rows: LeadCountRow[] }) =>
    requestJson<{ data: { id: string; contacts: number; adLeads: number; firstMonth: string; lastMonth: string } }>("/api/lead-counts", { method: "POST", body: JSON.stringify(payload) }),

  composioStatus: () => requestJson<ComposioStatus>("/api/composio/status"),

  syncComposio: (platformGroup: "ALL" | "META" | "GOOGLE" | "TIKTOK", dateFrom?: string, dateTo?: string, accountId?: string) => requestJson<{
    data: {
      dateFrom: string;
      dateTo: string;
      results: ComposioSyncResult[];
      errors: Array<{ platformGroup: "META" | "GOOGLE" | "TIKTOK"; message: string }>;
    };
  }>("/api/composio/sync", {
    method: "POST",
    body: JSON.stringify({ platformGroup, ...(dateFrom ? { dateFrom } : {}), ...(dateTo ? { dateTo } : {}), ...(accountId ? { accountId } : {}) }),
  }),

  previewPlatform: (platformGroup: PlatformImportGroup, fileName: string, csvText: string) =>
    requestJson<{ preview: PlatformImportPreview; duplicate: boolean }>("/api/imports/platform/preview", {
      method: "POST",
      body: JSON.stringify({ platformGroup, fileName, csvText }),
    }),

  commitPlatform: (id: string) => requestJson<{ imported: PlatformImportPreview; duplicate: boolean }>(`/api/imports/platform/${encodeURIComponent(id)}/commit`, {
    method: "POST",
    body: JSON.stringify({}),
  }),

  masterData: (year = "2026") => requestJson<{
    year: string;
    entries: MasterEntry[];
    counts: Record<MasterEntryType, number>;
    generatedAt: string;
  }>(`/api/master-data?${new URLSearchParams({ year })}`),

  deleteMasterEntry: (id: string) => requestJson<{ deleted: { id: string; entryType: string } }>(`/api/master-data/${encodeURIComponent(id)}`, {
    method: "DELETE",
  }),

  logout: async () => {
    const response = await fetch("/api/auth/logout", { method: "POST" });
    if (!response.ok) throw new ApiClientError(response.status, "LOGOUT_FAILED", "Sign out could not be completed.");
  },
};

export type { DetailRow };
